import { makeAutoObservable, runInAction } from "mobx";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { peekQueryClient } from "@/queries/client";
import {
  calendarConfigKey,
  holidayCalendarKey,
  holidayCalendarPeopleQuery,
  holidayCalendarsKey,
  holidayCalendarSuggestionsKey,
  myCalendarSourcesKey,
  myHolidayCalendarKey,
  type CalendarProvider,
} from "@/queries/calendars";
import { invalidateScoped, patchScoped } from "@/queries/invalidation";
import { GLOBAL_TENANT, type QueryScope } from "@/queries/scope";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type {
  ICalendarSource,
  IGoogleDomainWideConfig,
  IHolidayCalendar,
  IHolidayCalendarDate,
  IHolidayCalendarDateImportResult,
  IHolidayCalendarDatePatch,
  IHolidayCalendarDateWrite,
  IHolidayCalendarSuggestion,
  IHolidayCalendarSyncResult,
  IHolidayCalendarWrite,
  IOutlookDomainWideConfig,
} from "@/types/workspace";

/** The fields of a calendar write that change who it covers. */
const SCOPE_FIELDS: readonly (keyof IHolidayCalendarWrite)[] = [
  "applies_to",
  "match_by",
  "team",
  "country",
  "subdivision",
];

/** A domain-wide config entry as the infinite query holds it. */
type ConfigPages = InfiniteData<
  IGoogleDomainWideConfig | IOutlookDomainWideConfig | Record<string, never>,
  number | undefined
>;

/**
 * The calendar writes, and which one is in flight. Every calendar read goes
 * through Query (`app/queries/calendars.ts`, NG-2814): the writes here patch,
 * replace or invalidate those cached entries, so every screen that reads them
 * shows a confirmed write.
 */
export class WorkspaceCalendarsStore {
  activeHolidayCalendarId: string | null = null;
  isSavingMyHolidayCalendar = false;
  activeGoogleAction = "";
  activeOutlookAction = "";

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  // --- The cache the readers observe ---

  /** The scope `tenant`'s cached calendar reads live under, with the client; `null` before the session resolves. */
  private cache(tenant: string): { scope: QueryScope; client: QueryClient } | null {
    const scope = queryCacheScope(tenant, "workspace-calendars");
    const client = peekQueryClient();
    return scope && client ? { scope, client } : null;
  }

  /** Re-read the signed-in user's own calendar connections. Settles once observed ones are back. */
  private async refreshUserSources(): Promise<void> {
    const cache = this.cache(GLOBAL_TENANT);
    if (cache) await invalidateScoped(cache.client, cache.scope, "calendar-sources", "mine");
  }

  private patchUserSources(update: (sources: ICalendarSource[]) => ICalendarSource[]): void {
    const cache = this.cache(GLOBAL_TENANT);
    if (cache) patchScoped(cache.client, myCalendarSourcesKey(cache.scope), update);
  }

  /** Apply `update` to one calendar in `slug`'s cached list and its own entry. */
  private patchCalendar(
    slug: string,
    calendarId: string,
    update: (calendar: IHolidayCalendar) => IHolidayCalendar,
  ): void {
    const cache = this.cache(slug);
    if (!cache) return;
    patchScoped<IHolidayCalendar[]>(cache.client, holidayCalendarsKey(cache.scope), (calendars) =>
      calendars.map((item) => (item.id === calendarId ? update(item) : item)),
    );
    patchScoped<IHolidayCalendar>(
      cache.client,
      holidayCalendarKey(cache.scope, calendarId),
      update,
    );
  }

  /** Put a calendar the server returned into `slug`'s cached list and its own entry. */
  private storeCalendar(slug: string, calendar: IHolidayCalendar): void {
    const cache = this.cache(slug);
    if (!cache) return;
    patchScoped<IHolidayCalendar[]>(cache.client, holidayCalendarsKey(cache.scope), (calendars) =>
      calendars.some((item) => item.id === calendar.id)
        ? calendars.map((item) => (item.id === calendar.id ? calendar : item))
        : [calendar, ...calendars],
    );
    cache.client.setQueryData(holidayCalendarKey(cache.scope, calendar.id), calendar);
  }

  /** Re-read who no calendar covers: a calendar now covers, or stopped covering, people. */
  private refreshSuggestions(slug: string): void {
    const cache = this.cache(slug);
    if (!cache) return;
    invalidateScoped(cache.client, cache.scope, "holiday-calendars", "suggestions").catch(
      (error: unknown) => {
        console.warn("[workspace-calendars] re-reading the calendar suggestions failed", {
          slug,
          error,
        });
      },
    );
  }

  /** Set a provider's cached config to `config`, or to "not set up" with `null`. */
  private storeConfig(
    slug: string,
    provider: CalendarProvider,
    config: IGoogleDomainWideConfig | IOutlookDomainWideConfig | null,
  ): void {
    const cache = this.cache(slug);
    if (!cache) return;
    cache.client.setQueryData<ConfigPages>(calendarConfigKey(cache.scope, provider), {
      pages: [config ?? {}],
      pageParams: [undefined],
    });
  }

  /** A provider's cached config, or `null` when none is cached or set up. */
  private cachedConfig(slug: string, provider: CalendarProvider) {
    const cache = this.cache(slug);
    const first = cache?.client.getQueryData<ConfigPages>(calendarConfigKey(cache.scope, provider))
      ?.pages[0];
    return first && "id" in first ? first : null;
  }

  // --- The user's own calendar connections ---

  async deleteUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    await this.rootStore.workspaceCalendarService.deleteUserSource(sourceId);
    track("calendar_deleted", { source_id: sourceId, source });
    this.patchUserSources((sources) => sources.filter((item) => item.id !== sourceId));
  }

  async disconnectUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    await this.rootStore.workspaceCalendarService.disconnectUserSource(sourceId);
    track("calendar_disconnected", { source_id: sourceId, source });
    this.patchUserSources((sources) => sources.filter((item) => item.id !== sourceId));
  }

  /** Sync one connection now, then re-read the connections for its new sync status. */
  async syncUserSource(sourceId: string, source: AnalyticsSource = "page"): Promise<void> {
    await this.rootStore.workspaceCalendarService.syncUserSource(sourceId);
    track("calendar_synced", { source_id: sourceId, source });
    await this.refreshUserSources();
  }

  async getOAuthConnectUrl(
    provider: "google" | "outlook",
    returnPath?: string,
  ): Promise<{ authorization_url: string }> {
    return this.rootStore.workspaceCalendarService.getOAuthConnectUrl(provider, returnPath);
  }

  // --- The user's own public-holiday region ---

  async setMyHolidayCalendar(
    slug: string,
    data: { country: string; subdivision?: string },
    source: AnalyticsSource = "page",
  ): Promise<ICalendarSource> {
    this.isSavingMyHolidayCalendar = true;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.setMyHolidayCalendar(
        slug,
        data,
      );
      const cache = this.cache(slug);
      cache?.client.setQueryData(myHolidayCalendarKey(cache.scope), calendar);
      // A country code, not a person's detail. Before the refetch, as above.
      track("holiday_calendar_selected", {
        country: data.country,
        has_subdivision: Boolean(data.subdivision),
        source,
      });
      await this.refreshUserSources();
      return calendar;
    } finally {
      runInAction(() => {
        this.isSavingMyHolidayCalendar = false;
      });
    }
  }

  async deleteMyHolidayCalendar(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.isSavingMyHolidayCalendar = true;
    try {
      await this.rootStore.workspaceCalendarService.deleteMyHolidayCalendar(slug);
      track("holiday_calendar_cleared", { source });
      const cache = this.cache(slug);
      cache?.client.setQueryData(myHolidayCalendarKey(cache.scope), null);
      this.patchUserSources((sources) =>
        sources.filter((item) => item.source_type !== "public_holidays"),
      );
    } finally {
      runInAction(() => {
        this.isSavingMyHolidayCalendar = false;
      });
    }
  }

  // --- Admin-managed holiday calendars (M4–M6) ---

  async createHolidayCalendar(
    slug: string,
    data: IHolidayCalendarWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = "creating";
    try {
      const calendar = await this.rootStore.workspaceCalendarService.createHolidayCalendar(
        slug,
        data,
      );
      this.storeCalendar(slug, calendar);
      this.refreshSuggestions(slug);
      track("holiday_calendar_created", { calendar_id: calendar.id, source });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  // --- Suggested calendars (M15) ---

  /**
   * Hide one suggestion, optimistically — the card disappears on click and the
   * write reconciles behind it. On failure the card is put back where it was, so
   * the list never silently disagrees with what the server will serve next load.
   */
  async dismissHolidayCalendarSuggestion(
    slug: string,
    key: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.cache(slug);
    const entryKey = cache ? holidayCalendarSuggestionsKey(cache.scope) : null;
    const previous =
      cache && entryKey
        ? cache.client.getQueryData<IHolidayCalendarSuggestion[]>(entryKey)
        : undefined;
    if (cache && entryKey && previous) {
      await cache.client.cancelQueries({ queryKey: entryKey, exact: true });
      cache.client.setQueryData(
        entryKey,
        previous.filter((suggestion) => suggestion.key !== key),
      );
    }
    try {
      await this.rootStore.workspaceCalendarService.dismissHolidayCalendarSuggestion(slug, key);
      track("holiday_calendar_suggestion_dismissed", { source });
    } catch (error) {
      if (cache && entryKey && previous) cache.client.setQueryData(entryKey, previous);
      throw error;
    }
  }

  async updateHolidayCalendar(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    // Read before the write: `fields` is what this save changed.
    const cache = this.cache(slug);
    const before = cache
      ? (cache.client.getQueryData<IHolidayCalendar>(holidayCalendarKey(cache.scope, calendarId)) ??
        cache.client
          .getQueryData<IHolidayCalendar[]>(holidayCalendarsKey(cache.scope))
          ?.find((item) => item.id === calendarId))
      : undefined;
    const fields = changedFields(data, before);
    try {
      const calendar = await this.rootStore.workspaceCalendarService.updateHolidayCalendar(
        slug,
        calendarId,
        data,
      );
      this.storeCalendar(slug, calendar);
      // A scope edit changes who the calendar covers; that and turning it on or
      // off change who no calendar covers. A rename changes neither.
      const scopeEdit = SCOPE_FIELDS.some((field) => field in data);
      if (cache && scopeEdit) {
        invalidateScoped(cache.client, cache.scope, "holiday-calendar", calendarId, "people").catch(
          (error: unknown) => {
            console.warn("[workspace-calendars] re-reading a calendar's people failed", {
              slug,
              calendarId,
              error,
            });
          },
        );
      }
      if (scopeEdit || "is_active" in data) this.refreshSuggestions(slug);
      track("holiday_calendar_updated", { calendar_id: calendarId, fields, source });
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async deleteHolidayCalendar(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeHolidayCalendarId = calendarId;
    try {
      await this.rootStore.workspaceCalendarService.deleteHolidayCalendar(slug, calendarId);
      track("holiday_calendar_deleted", { calendar_id: calendarId, source });
      const cache = this.cache(slug);
      if (cache) {
        patchScoped<IHolidayCalendar[]>(
          cache.client,
          holidayCalendarsKey(cache.scope),
          (calendars) => calendars.filter((item) => item.id !== calendarId),
        );
        cache.client.removeQueries({ queryKey: holidayCalendarKey(cache.scope, calendarId) });
      }
      this.refreshSuggestions(slug);
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  /**
   * How many people a calendar covers, for its delete confirmation: from the
   * cached people when they are fresh, else read now (and cached for the page).
   */
  async countHolidayCalendarPeople(slug: string, calendarId: string): Promise<number> {
    const cache = this.cache(slug);
    const page = cache
      ? await cache.client.fetchQuery(
          holidayCalendarPeopleQuery(
            this.rootStore.workspaceCalendarService,
            cache.scope,
            calendarId,
          ),
        )
      : await this.rootStore.workspaceCalendarService.getHolidayCalendarPeople(slug, calendarId);
    return page.total_count;
  }

  /**
   * Re-read every cached dates list of one calendar (each filter and sort the
   * panel has read): after a write that adds, removes or re-sources dates the
   * panel cannot patch row by row. Settles once the one on screen is back; a
   * failed re-read is logged by the cache.
   */
  async refreshHolidayCalendarDates(slug: string, calendarId: string): Promise<void> {
    const cache = this.cache(slug);
    if (!cache) return;
    await invalidateScoped(cache.client, cache.scope, "holiday-calendar", calendarId, "dates");
  }

  async createHolidayCalendarDate(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarDateWrite,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarDate> {
    const created = await this.rootStore.workspaceCalendarService.createHolidayCalendarDate(
      slug,
      calendarId,
      data,
    );
    track("holiday_date_created", { calendar_id: calendarId, date_id: created.id, source });
    // Adding a date is publish-gated content, so the server flips
    // has_unpublished_changes — mirror it so the Publish button lights up
    // without a refetch of the calendar.
    this.patchCalendar(slug, calendarId, (item) => ({ ...item, has_unpublished_changes: true }));
    // Re-read rather than splice the new row in: it may fall outside the panel's
    // filter or search, and the server owns the ordering.
    await this.refreshHolidayCalendarDates(slug, calendarId);
    return created;
  }

  async importHolidayCalendarDates(
    slug: string,
    calendarId: string,
    file: File,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarDateImportResult> {
    const result = await this.rootStore.workspaceCalendarService.importHolidayCalendarDates(
      slug,
      calendarId,
      file,
    );
    track("holiday_dates_imported", { calendar_id: calendarId, count: result.created, source });
    // Same publish gate as a hand-added date — but only if the import actually
    // wrote a row; a file whose dates were all already there changes nothing.
    if (result.created > 0) {
      this.patchCalendar(slug, calendarId, (item) => ({ ...item, has_unpublished_changes: true }));
      await this.refreshHolidayCalendarDates(slug, calendarId);
    }
    return result;
  }

  async patchHolidayCalendarDate(
    slug: string,
    calendarId: string,
    dateId: string,
    data: IHolidayCalendarDatePatch,
    source: AnalyticsSource = "page",
    // The row as the caller shows it: the dates live in the panel, not this store.
    // Without it, every key the patch sends counts as changed.
    before?: IHolidayCalendarDate,
  ): Promise<IHolidayCalendarDate> {
    const updated = await this.rootStore.workspaceCalendarService.patchHolidayCalendarDate(
      slug,
      calendarId,
      dateId,
      data,
    );
    // An undefined value is never sent, so it can't be a change.
    const sent = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined),
    );
    track("holiday_date_updated", {
      calendar_id: calendarId,
      date_id: dateId,
      fields: changedFields(sent, before),
      source,
    });
    return updated;
  }

  async deleteHolidayCalendarDate(
    slug: string,
    calendarId: string,
    dateId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workspaceCalendarService.deleteHolidayCalendarDate(
      slug,
      calendarId,
      dateId,
    );
    track("holiday_date_deleted", { calendar_id: calendarId, date_id: dateId, source });
  }

  // --- The live ICS URL subscription (M9) ---

  /** Connect a feed and pull it once. The calendar carries its subscription, so
   *  patch it in rather than refetching the list. */
  async connectHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    feedUrl: string,
    source: AnalyticsSource = "page",
  ): Promise<number> {
    const { subscription, synced_count } =
      await this.rootStore.workspaceCalendarService.connectHolidayCalendarFeed(
        slug,
        calendarId,
        feedUrl,
      );
    this.patchCalendar(slug, calendarId, (item) => ({ ...item, subscription }));
    await this.refreshHolidayCalendarDates(slug, calendarId);
    // The feed URL is never a prop.
    track("holiday_calendar_feed_connected", {
      calendar_id: calendarId,
      count: synced_count,
      source,
    });
    return synced_count;
  }

  async syncHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendarSyncResult> {
    const result = await this.rootStore.workspaceCalendarService.syncHolidayCalendarFeed(
      slug,
      calendarId,
    );
    this.patchCalendar(slug, calendarId, (item) => ({
      ...item,
      subscription: result.subscription,
    }));
    await this.refreshHolidayCalendarDates(slug, calendarId);
    track("holiday_calendar_feed_synced", { calendar_id: calendarId, source });
    return result;
  }

  /** Disconnect the feed. Its dates stay behind as manual ones (§7.4.4), so the
   *  dates are re-read to pick up their new Source. */
  async disconnectHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<number> {
    const { kept } = await this.rootStore.workspaceCalendarService.disconnectHolidayCalendarFeed(
      slug,
      calendarId,
    );
    this.patchCalendar(slug, calendarId, (item) => ({ ...item, subscription: null }));
    await this.refreshHolidayCalendarDates(slug, calendarId);
    track("holiday_calendar_feed_disconnected", { calendar_id: calendarId, count: kept, source });
    return kept;
  }

  async publishHolidayCalendar(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.publishHolidayCalendar(
        slug,
        calendarId,
      );
      track("holiday_calendar_published", { calendar_id: calendarId, source });
      this.storeCalendar(slug, calendar);
      // Publishing makes every unpublished date live, so the Dates panel's rows
      // drop their "Not published" badge only once they are read again.
      await this.refreshHolidayCalendarDates(slug, calendarId);
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  async discardHolidayCalendarDates(
    slug: string,
    calendarId: string,
    source: AnalyticsSource = "page",
  ): Promise<IHolidayCalendar> {
    this.activeHolidayCalendarId = calendarId;
    try {
      const calendar = await this.rootStore.workspaceCalendarService.discardHolidayCalendarDates(
        slug,
        calendarId,
      );
      track("holiday_dates_discarded", { calendar_id: calendarId, source });
      this.storeCalendar(slug, calendar);
      // Discard removes the unpublished rows the panel is showing.
      await this.refreshHolidayCalendarDates(slug, calendarId);
      return calendar;
    } finally {
      runInAction(() => {
        this.activeHolidayCalendarId = null;
      });
    }
  }

  // --- Domain-wide sync (Google and Outlook) ---

  async createGoogleConfig(
    slug: string,
    data: { service_account_key: string; domain: string },
    source: AnalyticsSource = "page",
  ): Promise<IGoogleDomainWideConfig> {
    this.activeGoogleAction = "create";
    try {
      const config = await this.rootStore.workspaceCalendarService.createGoogleDomainWideConfig(
        slug,
        data,
      );
      this.storeConfig(slug, "google", config);
      // Before the refetch, so a failed reload doesn't drop it.
      track("calendar_connected", { provider: "google", scope: "workspace", source });
      await this.refreshUserSources();
      return config;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async updateGoogleConfig(
    slug: string,
    data: Partial<{ is_enabled: boolean; domain: string }>,
    source: AnalyticsSource = "page",
  ): Promise<IGoogleDomainWideConfig> {
    this.activeGoogleAction = "update";
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(data, this.cachedConfig(slug, "google") ?? undefined);
    try {
      const config = await this.rootStore.workspaceCalendarService.updateGoogleDomainWideConfig(
        slug,
        data,
      );
      this.storeConfig(slug, "google", config);
      track("calendar_config_updated", { provider: "google", fields, source });
      if (data.is_enabled !== undefined) {
        await this.refreshUserSources();
      }
      return config;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async deleteGoogleConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeGoogleAction = "delete";
    try {
      await this.rootStore.workspaceCalendarService.deleteGoogleDomainWideConfig(slug);
      this.storeConfig(slug, "google", null);
      track("calendar_config_deleted", { provider: "google", source });
      await this.refreshUserSources();
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async testGoogleConfig(
    slug: string,
    testEmail: string,
    source: AnalyticsSource = "page",
  ): Promise<{ success: boolean; message: string }> {
    this.activeGoogleAction = "test";
    try {
      const result = await this.rootStore.workspaceCalendarService.testGoogleDomainWideConfig(
        slug,
        {
          test_email: testEmail,
        },
      );
      // The test address is never a prop, only whether it worked.
      track("calendar_config_tested", { provider: "google", success: result.success, source });
      return result;
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async syncGoogleConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeGoogleAction = "sync";
    try {
      await this.rootStore.workspaceCalendarService.syncGoogleDomainWideConfig(slug);
      track("calendar_config_synced", { provider: "google", source });
    } finally {
      runInAction(() => {
        this.activeGoogleAction = "";
      });
    }
  }

  async createOutlookConfig(
    slug: string,
    data: { tenant_id: string; client_id: string; client_secret: string; domain: string },
    source: AnalyticsSource = "page",
  ): Promise<IOutlookDomainWideConfig> {
    this.activeOutlookAction = "create";
    try {
      const config = await this.rootStore.workspaceCalendarService.createOutlookDomainWideConfig(
        slug,
        data,
      );
      this.storeConfig(slug, "outlook", config);
      // Before the refetch, so a failed reload doesn't drop it.
      track("calendar_connected", { provider: "outlook", scope: "workspace", source });
      await this.refreshUserSources();
      return config;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async updateOutlookConfig(
    slug: string,
    data: Partial<{ is_enabled: boolean; domain: string }>,
    source: AnalyticsSource = "page",
  ): Promise<IOutlookDomainWideConfig> {
    this.activeOutlookAction = "update";
    // Read before the write: `fields` is what this save changed.
    const fields = changedFields(data, this.cachedConfig(slug, "outlook") ?? undefined);
    try {
      const config = await this.rootStore.workspaceCalendarService.updateOutlookDomainWideConfig(
        slug,
        data,
      );
      this.storeConfig(slug, "outlook", config);
      track("calendar_config_updated", { provider: "outlook", fields, source });
      if (data.is_enabled !== undefined) {
        await this.refreshUserSources();
      }
      return config;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async deleteOutlookConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeOutlookAction = "delete";
    try {
      await this.rootStore.workspaceCalendarService.deleteOutlookDomainWideConfig(slug);
      this.storeConfig(slug, "outlook", null);
      track("calendar_config_deleted", { provider: "outlook", source });
      await this.refreshUserSources();
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async testOutlookConfig(
    slug: string,
    testEmail: string,
    source: AnalyticsSource = "page",
  ): Promise<{ success: boolean; message: string }> {
    this.activeOutlookAction = "test";
    try {
      const result = await this.rootStore.workspaceCalendarService.testOutlookDomainWideConfig(
        slug,
        {
          test_email: testEmail,
        },
      );
      // The test address is never a prop, only whether it worked.
      track("calendar_config_tested", { provider: "outlook", success: result.success, source });
      return result;
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }

  async syncOutlookConfig(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeOutlookAction = "sync";
    try {
      await this.rootStore.workspaceCalendarService.syncOutlookDomainWideConfig(slug);
      track("calendar_config_synced", { provider: "outlook", source });
    } finally {
      runInAction(() => {
        this.activeOutlookAction = "";
      });
    }
  }
}
